import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { RotateCw, Webhook as WebhookIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type DeliveriesPage, useWebhookActions } from '../../api/webhooks.api';
import { formatDuration } from '../../lib/format';
import type { Webhook, WebhookDelivery } from '../../schemas/webhook.schema';
import { ManagedByCacBadge } from '../shared/ManagedByCac';

const LIMIT = 20;

function DeliveryBadge({ d }: { d: WebhookDelivery | null }) {
  if (!d) return <span className="text-xs text-muted-foreground">never</span>;
  return (
    <span className="flex items-center gap-2">
      <StatusBadge tone={d.status === 'DELIVERED' ? 'success' : 'danger'}>
        {d.status === 'DELIVERED' ? 'delivered' : 'failed'}
      </StatusBadge>
      <span className="text-xs text-muted-foreground">
        {d.responseStatus ?? d.error} · {new Date(d.requestedAt).toLocaleString()}
      </span>
    </span>
  );
}

export function WebhooksCard({ appId }: { appId: string }) {
  const api = useWebhookActions();
  const { showSnackbar } = useSnackbar();
  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [deliveries, setDeliveries] = useState<DeliveriesPage | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, d] = await Promise.all([
        api.list(appId),
        api.deliveries({ appId, page, limit: LIMIT }),
      ]);
      setHooks(h);
      setDeliveries(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load webhooks'));
    }
  }, [api, appId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const redeliver = useCallback(
    async (d: WebhookDelivery) => {
      try {
        await api.redeliver(appId, d.id);
        showSnackbar({
          message: `Queued ${d.eventId} again for ${d.endpointName ?? 'webhook'}`,
          variant: 'success',
        });
        await load();
      } catch {
        showSnackbar({ message: 'Could not redeliver this event', variant: 'error' });
      }
    },
    [api, appId, showSnackbar, load],
  );

  const hookColumns = useMemo<ColumnDef<Webhook>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.managedBy === 'cac' ? <ManagedByCacBadge /> : null}
            {row.original.disabledAt ? <StatusBadge tone="neutral">disabled</StatusBadge> : null}
          </span>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.url}</code>,
      },
      {
        id: 'events',
        header: 'Events',
        cell: ({ row }) => (
          <span className="flex flex-wrap gap-1">
            {row.original.events.map((e) => (
              <StatusBadge key={e} tone="info" dot={false}>
                {e}
              </StatusBadge>
            ))}
          </span>
        ),
      },
      {
        id: 'last',
        header: 'Last delivery',
        cell: ({ row }) => <DeliveryBadge d={row.original.lastDelivery} />,
      },
      {
        id: 'counts',
        header: '24h',
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {row.original.counts24h.delivered} ok · {row.original.counts24h.failed} failed
          </span>
        ),
      },
    ],
    [],
  );

  const deliveryColumns = useMemo<ColumnDef<WebhookDelivery>[]>(
    () => [
      {
        accessorKey: 'eventId',
        header: 'Event',
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.eventId}</code>,
      },
      { accessorKey: 'endpointName', header: 'Webhook' },
      { accessorKey: 'attempt', header: 'Attempt' },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.status === 'DELIVERED' ? 'success' : 'danger'}>
            {row.original.status === 'DELIVERED' ? 'delivered' : 'failed'}
          </StatusBadge>
        ),
      },
      {
        id: 'response',
        header: 'Response',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.responseStatus ?? row.original.error ?? '—'}
            {row.original.durationMs ? ` · ${formatDuration(row.original.durationMs / 1000)}` : ''}
          </span>
        ),
      },
      {
        id: 'when',
        header: 'Requested at',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.requestedAt).toLocaleString()}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button variant="outline" size="sm" onClick={() => redeliver(row.original)}>
            <RotateCw /> Redeliver
          </Button>
        ),
      },
    ],
    [redeliver],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhooks</CardTitle>
        <CardDescription>
          Declared in the product’s cac/stack.ts with fisto.Webhook; Fisto delivers file and quota
          events signed with the secret it generated on apply.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        {hooks && hooks.length === 0 ? (
          <EmptyState
            icon={<WebhookIcon className="h-5 w-5" />}
            title="No webhooks declared"
            description="Add new fisto.Webhook(app, name, { url, events, secretVar }) to the product’s cac/stack.ts and apply it."
          />
        ) : (
          <DataTable
            columns={hookColumns}
            data={hooks ?? []}
            isLoading={hooks === null}
            hidePagination
            aria-label="Webhooks"
          />
        )}
        {hooks && hooks.length > 0 ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Recent deliveries</h3>
            <DataTable
              columns={deliveryColumns}
              data={deliveries?.data ?? []}
              isLoading={deliveries === null}
              hidePagination
              aria-label="Webhook deliveries"
              empty={
                <p className="py-6 text-center text-sm text-muted-foreground">No deliveries yet</p>
              }
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!deliveries?.meta.hasPrev}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!deliveries?.meta.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}
